/**
 * MODULE: GlobalSalesTargetSetHost.tsx
 * PURPOSE: App-level generative quota-set modal from assistant, My quota, and onboarding.
 * DEPENDENCIES: salesTargetSetBridge, SalesTargetSelfDialog, useShellAssistantOptional
 * PUBLIC API: GlobalSalesTargetSetHost
 * SIDE_EFFECTS: Opens modal; may open shell assistant dock when coachInAssistant
 * AGENT SAFETY: SAFE
 * RECENT: 2026-06-21 [2.2.1458] — useShellAssistantOptional; mount inside ShellAssistantProvider in AppLayout.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  subscribeOpenSalesTargetSet,
  triggerQuotaPlanningLearningRefresh,
  type SalesTargetSetOpenOptions,
  type SalesTargetSetPrefill,
} from '@shared/lib/frontend/utils/salesTargetSetBridge';
import { useToast } from '@design-system/components';
import { useCurrentUser } from '@core/auth';
import { SalesTargetSelfDialog } from '@domain/sales-target-management/frontend';
import { useShellAssistantOptional } from '../../layout/shellAssistant/ShellAssistantContext';

export function GlobalSalesTargetSetHost() {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<SalesTargetSetPrefill | undefined>();
  const [coachInAssistant, setCoachInAssistant] = useState(false);
  const { user } = useCurrentUser();
  const shellAssistant = useShellAssistantOptional();
  const { showToast } = useToast();

  useEffect(
    () =>
      subscribeOpenSalesTargetSet((options: SalesTargetSetOpenOptions) => {
        setPrefill(options.prefill);
        setCoachInAssistant(Boolean(options.coachInAssistant));
        setOpen(true);
        if (options.coachInAssistant && shellAssistant) {
          shellAssistant.openGlobalAssistant(
            { path: '/crm/my-quota', title: 'My quota' },
            {
              prefillContext:
                'Help me set a realistic quota for this quarter based on my pipeline and territory.',
              autoSend: false,
            }
          );
        }
      }),
    [shellAssistant]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setPrefill(undefined);
    setCoachInAssistant(false);
  }, []);

  const handleSuccess = useCallback(() => {
    triggerQuotaPlanningLearningRefresh();
    handleClose();
    showToast({
      message: coachInAssistant
        ? 'Quota saved. Ask the assistant how you are tracking against your new target.'
        : 'Quota saved.',
      variant: 'success',
    });
  }, [coachInAssistant, handleClose, showToast]);

  if (!user?.id) return null;

  return (
    <SalesTargetSelfDialog
      open={open}
      onClose={handleClose}
      onSaved={handleSuccess}
      userId={user.id}
      defaultFiscalYear={prefill?.fiscalYear}
      defaultFiscalQuarter={prefill?.fiscalQuarter}
      initialTargetAmount={prefill?.targetAmount}
      showAiCoachStrip
      coachInAssistant={coachInAssistant}
    />
  );
}
